import type { StatusMap } from '@bemor/web-kit/ui';

/**
 * Admin's own status maps, for codes only Admin shows (the shared domains live in web-kit's `STATUSES`). Tones are
 * the design's (UI-UX rule 0.5); labels sentence case. Codes from the generated API types.
 */
export const ADMIN_STATUSES = {
    /** BM-127 decision 2: the charge's open and lost disputes exceed its amount; staff check it against Netevia. */
    disputeFlag: {
        over_disputed: ['warning', 'Over-disputed'],
    },
    /** BM-127: a processor chargeback row's match state. */
    chargebackRow: {
        matched: ['success', 'Matched'],
        unmatched: ['warning', 'Unmatched'],
        skipped: ['neutral', 'Skipped'],
    },
    /** Admin work items (queue rows). */
    queueItem: {
        open: ['info', 'Open'],
        done: ['success', 'Done'],
    },
    /** Case priority. */
    priority: {
        low: ['neutral', 'Low'],
        normal: ['neutral', 'Normal'],
        high: ['warning', 'High'],
        urgent: ['danger', 'Urgent'],
    },
    /** A flag's default, or archived. */
    flag: {
        on: ['success', 'On'],
        off: ['neutral', 'Off'],
        archived: ['neutral', 'Archived'],
    },
    flagKind: {
        release: ['info', 'Release'],
        kill_switch: ['warning', 'Kill switch'],
    },
    /** BM-105: whose flag it is. */
    flagScope: {
        platform: ['neutral', 'Platform'],
        tenant: ['info', 'This tenant'],
    },
    /** BM-105: whether the platform opened a template default to tenant overrides. */
    templateOverridable: {
        open: ['success', 'Open'],
        locked: ['neutral', 'Locked'],
    },
    /** A notification of the log (BM-103, Nexroll's Notification Logs). */
    notification: {
        pending: ['warning', 'Pending'],
        sent: ['success', 'Sent'],
        failed: ['danger', 'Failed'],
        digested: ['neutral', 'Digested'],
    },
    /** BM-103: a template switched on or off for the tenant. */
    templateActive: {
        active: ['success', 'Active'],
        inactive: ['neutral', 'Inactive'],
    },
    /** A notification template's copy (design: Overridden info, Default neutral). */
    template: {
        default: ['neutral', 'Default'],
        overridden: ['info', 'Overridden'],
        locked: ['neutral', 'Locked by the platform'], // BM-105
    },
    /** The onboarding application (BM-40). */
    application: {
        draft: ['neutral', 'Draft'],
        submitted: ['pending', 'Submitted'],
        in_review: ['pending', 'In review'],
        needs_info: ['warning', 'Needs info'],
        approved: ['success', 'Approved'],
        rejected: ['danger', 'Rejected'],
    },
    /** A KYC document on the application (BM-59; design `backoffice/merchant` DOC pills). */
    document: {
        uploaded: ['pending', 'Uploaded'],
        accepted: ['success', 'Accepted'],
        rejected: ['danger', 'Rejected'],
        superseded: ['neutral', 'Replaced'],
        missing: ['neutral', 'Missing'],
    },
    /** BM-63: the tax provider transactions. */
    taxTransaction: {
        pending: ['pending', 'Pending'],
        committed: ['success', 'Committed'],
        failed: ['danger', 'Failed'],
        frozen: ['warning', 'Frozen'],
    },
    /** BM-62: a charge's reserve in the release schedule. */
    reserveState: {
        held: ['pending', 'Held'],
        due: ['warning', 'Due'],
        released: ['success', 'Released'],
    },
    /** BM-62: a pricing card's side. */
    cardSide: {
        fee: ['info', 'Fee'],
        cost: ['neutral', 'Cost'],
    },
    /** BM-67: a warm-up run of the tax rate store. */
    taxRateRun: {
        queued: ['pending', 'Queued'],
        running: ['info', 'Running'],
        done: ['success', 'Done'],
        failed: ['danger', 'Failed'],
        stopped: ['neutral', 'Stopped'],
    },
    taxKind: {
        sale: ['info', 'Sale'],
        refund: ['neutral', 'Refund'],
        chargeback: ['warning', 'Chargeback'],
        void: ['neutral', 'Void'],
        negation: ['neutral', 'Negation'],
    },
    /** A held payment's review (BM-51 § 7): `open` stands for a null decision. */
    chargeReview: {
        open: ['pending', 'Open'],
        approved: ['success', 'Approved'],
        rejected: ['danger', 'Rejected'],
        expired: ['neutral', 'Expired'],
    },
    /** BM-125 decision 20: a case in Risk's review queue. */
    riskCase: {
        open: ['pending', 'Open'],
        approved: ['success', 'Approved'],
        declined: ['danger', 'Declined'],
        bypassed: ['warning', 'Bypassed'],
        expired: ['neutral', 'Expired'],
    },
    /** review: a risk verdict; cvv: a hold Payments parked (and every review from before the merge). */
    riskCaseKind: {
        review: ['info', 'Risk review'],
        cvv: ['neutral', 'Payment check'],
        sanctions: ['danger', 'Sanctions'],
        pep: ['warning', 'PEP'],
    },
    /** BM-125 slice 4: a sanctions or PEP case: cleared (a false match, whitelisted) or confirmed. */
    screeningCase: {
        open: ['pending', 'Open'],
        cleared: ['success', 'Cleared'],
        confirmed: ['danger', 'Confirmed'],
    },
    /** Who a sanctions case screened. */
    screeningSubjectKind: {
        buyer: ['neutral', 'Buyer'],
        owner: ['info', 'Owner'],
        business: ['info', 'Business name'],
    },
    /** A merchant owner or business name as last screened. */
    screeningSubject: {
        clear: ['success', 'Clear'],
        hit: ['danger', 'Possible match'],
        whitelisted: ['neutral', 'Whitelisted'],
    },
    /** BM-125 slice 3: a merchant's monitoring state from the PT rules (the worst of its latest rows). */
    riskMonitoring: {
        ok: ['success', 'OK'],
        watch: ['warning', 'Watch'],
        action: ['danger', 'Action'],
    },
    /** A remedial measure's state. */
    riskMeasure: {
        proposed: ['pending', 'Proposed'],
        applied: ['danger', 'Applied'],
        rejected: ['neutral', 'Rejected'],
        lifted: ['success', 'Lifted'],
    },
    riskMeasureKind: {
        payout_pause: ['danger', 'Payout pause'],
        capture_hold: ['warning', 'Capture hold'],
        extra_reserve: ['warning', 'Extra reserve'],
        volume_cap: ['warning', 'Volume cap'],
        suspension: ['danger', 'Suspension'],
    },
    /** A monitoring case. */
    riskMonitoringCase: {
        open: ['pending', 'Open'],
        closed: ['neutral', 'Closed'],
    },
    /** bm-111 § 10: a checkout session's state (chk_sessions.state). */
    session: {
        open: ['info', 'Open'],
        quoted: ['info', 'Quoted'],
        paying: ['pending', 'Paying'],
        pending: ['pending', 'Pending'],
        in_review: ['pending', 'In review'],
        paid: ['success', 'Paid'],
        declined: ['danger', 'Declined'],
        expired: ['neutral', 'Expired'],
        cancelled: ['neutral', 'Canceled'],
        refused: ['danger', 'Refused'],
        saved: ['success', 'Saved'],
    },
    /** BM-145: a Ledger provisioning step (Nexroll's provisioning card words). */
    ledgerStep: {
        pending: ['neutral', 'Not started'],
        running: ['info', 'In progress'],
        done: ['success', 'Complete'],
        failed: ['danger', 'Failed'],
    },
    /** bm-111 § 10: the verdict of a Risk check. */
    riskVerdict: {
        allow: ['success', 'Allow'],
        review: ['warning', 'Review'],
        decline: ['danger', 'Decline'],
    },
} as const satisfies Record<string, StatusMap>;
